import { isServer, QueryClient, QueryCache } from "@tanstack/react-query";
import { createLogger } from "@brev-io/common/src/utils/logger";
import { DeprecatedActionError } from "@brev-io/common/src/server-actions/deprecated";
import { isServerActionVersionSkewError } from "@/components/route-error-classification";
import { isSoftRefreshEnabled } from "@/lib/version-update-flag";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

const logger = createLogger("QueryClient");

/**
 * Chunk/deployment error patterns across browsers and bundlers.
 * - Loading chunk: Webpack dynamic import failure
 * - ChunkLoadError: Webpack specific error name
 * - dynamically imported module: Vite/esbuild dynamic import
 * - Importing a module script failed: Safari's chunk error variant
 */
const CHUNK_ERROR_PATTERNS = [
    'Loading chunk',
    'ChunkLoadError',
    'dynamically imported module',
    'Importing a module script failed',
] as const;

/**
 * Defensively extract name and message from any error-like value.
 * The unhandledrejection handler can receive non-Error values (strings, plain objects).
 */
function extractErrorDetails(error: unknown): { name: string; message: string } {
    if (error instanceof Error) {
        return { name: error.name ?? '', message: error.message ?? '' };
    }
    if (typeof error === 'object' && error !== null) {
        const e = error as Record<string, unknown>;
        return {
            name: typeof e.name === 'string' ? e.name : '',
            message: typeof e.message === 'string' ? e.message : '',
        };
    }
    return { name: '', message: typeof error === 'string' ? error : '' };
}

/**
 * Detects if an error is a stale deployment error (NOT a transient network failure).
 * Covers both stale chunks (bundler assets) and stale server actions (Next.js Server Actions).
 * Used to trigger page reload only when the app is genuinely outdated.
 * Handles non-Error rejection reasons from unhandledrejection events.
 *
 * The server-action skew classification is delegated to
 * `isServerActionVersionSkewError` in route-error-classification.ts — the single
 * source of truth (its regex also catches the "older or newer deployment" text
 * the old ad-hoc lowercase match here missed). Chunk-load patterns and the
 * Failed-to-fetch+context heuristic stay here: they're bundler/transport
 * concerns, not route-error classification.
 */
export function isStaleDeploymentError(error: unknown, context?: string): boolean {
    const { name, message } = extractErrorDetails(error);
    if (!name && !message) return false;

    // Chunk-load failures (bundler assets) — kept local; Webpack sets the name
    // on ChunkLoadError, and the patterns cover the message variants.
    if (name === 'ChunkLoadError') return true;
    if (CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern))) return true;

    // Server-action version skew — single source of truth in
    // route-error-classification.ts. Pass the real Error when we have one (so
    // its stack is matched too); otherwise synthesize a normalized Error from
    // the extracted name/message, since an unhandledrejection reason can be a
    // string or plain object rather than an Error.
    const errForClassifier =
        error instanceof Error ? error : Object.assign(new Error(message), { name });
    if (isServerActionVersionSkewError(errForClassifier)) return true;

    // "Failed to fetch" from a TypeError can indicate the Server Action endpoint
    // no longer exists after a deploy. It also occurs from transient network
    // failures or unrelated fetches (WebSocket token, Cloudflare session
    // probe, etc.), so only match inside a known fetch context, to avoid false
    // positives from the global unhandledrejection listener.
    //
    // Recognized contexts:
    //  - Replication push/pull ("goals-push:...", "replication-pull:<label>"):
    //    server-action calls, so a dead endpoint after a deploy surfaces here.
    //  - TanStack Query fetches ('query', BRE-6026): the same TypeError from a
    //    query failing mid-deploy was classified `other`, so it neither
    //    escalated the version-update toast nor appeared in stale-deploy
    //    metrics (seen in the BRE-5972 investigation: `gamification`,
    //    `suggestedCheckins`, `cadenceHealthScore`). Transient blips are
    //    filtered BEFORE this classifier runs: queries use
    //    `networkMode: 'online'` (no fetch, and retries pause, while the
    //    browser is offline) and the QueryCache onError fires only after the
    //    retry policy is exhausted. The retry callbacks themselves pass NO
    //    context, deliberately — a bare "Failed to fetch" still retries, and
    //    only the post-retry failure is classified as skew.
    const isServerActionContext =
        typeof context === 'string' &&
        (context.includes('-push:') || context.includes('-pull:'));
    const isQueryContext = context === 'query';

    if (
        name === 'TypeError' &&
        message.toLowerCase().includes('failed to fetch') &&
        // Offline fetch failures are connectivity, not deploy skew (BRE-9210).
        (typeof navigator === 'undefined' || navigator.onLine !== false) &&
        (isServerActionContext || isQueryContext)
    ) {
        return true;
    }

    return false;
}

/**
 * Session storage key and TTL for stale deployment reload guard.
 * Stores a timestamp instead of a boolean so the guard naturally expires,
 * allowing a fresh reload attempt after the TTL (deployment may have propagated).
 * Within the TTL window, a second stale error shows the banner instead of reloading.
 */
const STALE_RELOAD_KEY = 'stale-deployment-reload-at';
const STALE_RELOAD_TTL_MS = 30_000;

/**
 * Handle stale deployment detection.
 *
 * Two behaviors, selected by isSoftRefreshEnabled() — soft-refresh on for all
 * non-prod environments, off in prod (see version-update-flag.ts):
 *  - Non-prod → mark the skew on the sync-status store so <VersionUpdateToast>
 *    surfaces; NO automatic reload. User-triggered failures (mutations, pushes,
 *    click-driven unhandled rejections) additionally bump the blocking counter.
 *  - Prod     → TTL-gated reload-then-banner. The reload picks up the new
 *    bundle and preserves IndexedDB; checkpoint-based replication reconciles
 *    after load. It does NOT wipe local data — only a schema bump
 *    (RXDB_SCHEMA_VERSION) does that, via migrateRxDBSchema().
 *
 * Exported so replication handlers can also trigger this.
 */
export function handleStaleDeploymentError(error: unknown, context: string): void {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return;
    }
    if (!isStaleDeploymentError(error, context)) return;

    if (isSoftRefreshEnabled()) {
        // Soft path: surface the toast, no reload. Classify whether the failure
        // hit something the user was actively doing so the toast can escalate
        // from a passive "Update available" hint to a blocking "refresh to
        // continue" prompt. Mutations are user-triggered; replication PUSH
        // operations carry pending user writes (contexts like "goals-push:..."
        // and "replication-push:<label>") so they count too; unhandled
        // rejections are treated as user-triggered because the global handler
        // exists precisely to catch fire-and-forget action calls from
        // user-driven mutations (e.g. the deletionWork IIFE in goal mutations).
        // Background replication PULLs and TanStack query refetches are passive.
        const isUserTriggered =
            context === 'mutation' ||
            context.includes('-push:') ||
            context === 'unhandled-rejection';

        // Surface which renamed action caught this old tab, when the shim told
        // us (DeprecatedActionError carries the replacement name).
        const replacement =
            error instanceof DeprecatedActionError ? error.replacement : undefined;

        logger.info(
            `Stale deployment detected (${context}), surfacing soft-refresh toast`,
            { isUserTriggered, replacement },
        );
        import('@/rxdb/sync-status-store').then(({ useSyncStatusStore }) => {
            const store = useSyncStatusStore.getState();
            store.markStaleDeployment(replacement);
            if (isUserTriggered) store.recordStaleActionFailure(replacement);
        }).catch((err) => {
            // console.error intentionally — the structured logger may itself be
            // part of the stale deployment that failed to load.
            console.error('Failed to signal stale deployment to UI:', err);
        });
        return;
    }

    // Legacy path: TTL-gated reload-then-banner. Unchanged from prior behavior.
    const lastReloadAt = Number(sessionStorage.getItem(STALE_RELOAD_KEY) ?? 0);
    const hasReloadedRecently =
        Number.isFinite(lastReloadAt) &&
        Date.now() - lastReloadAt < STALE_RELOAD_TTL_MS;

    if (!hasReloadedRecently) {
        logger.info(`Stale deployment detected (${context}), reloading to pick up the new bundle...`);
        // Reload ONLY — do NOT wipe local data. A stale deploy means the JS
        // bundle / Server Action IDs rotated; it tells us nothing about whether
        // the local RxDB (IndexedDB) cache is valid. Replication is
        // checkpoint-based, so after the new bundle loads the existing
        // checkpoint resumes and reconciles server truth without a cold
        // full-pull: the goals pull runs a dual-stream delta — an active
        // visibility-filtered stream plus a "revoked keys" stream that issues
        // doc.remove for any UUID the server no longer exposes (revoked /
        // soft-deleted) — see goalsReplication in replication-registry.ts.
        //
        // Schema migrations remain the ONLY trigger that wipes IndexedDB: a real
        // schema change bumps RXDB_SCHEMA_VERSION (rxdb-constants.ts) and
        // migrateRxDBSchema() (RxDBProvider.tsx) deletes the databases on that
        // version mismatch on its own. The old code here additionally cleared
        // 'brev_rxdb_schema_version' to force that wipe on every stale-deploy
        // reload — redundant when the schema changed, and a multi-second
        // blocking cold re-pull of perfectly valid data when it didn't.
        sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now()));
        window.location.reload();
    } else {
        // Already reloaded recently but still getting stale errors.
        // This can happen if the CDN/edge cache hasn't fully propagated yet.
        // Signal to the UI to show a persistent "please refresh" banner.
        import('@/rxdb/sync-status-store').then(({ useSyncStatusStore }) => {
            useSyncStatusStore.getState().markStaleDeployment();
        }).catch((err) => {
            console.error('Failed to signal stale deployment to UI:', err);
        });
    }
}

// Global safety net: catch stale deployment errors that escape local try-catch blocks.
// Covers fire-and-forget server action calls (e.g., deletionWork IIFE in goal mutations,
// refreshGoalIntegrationConnection in onSuccess) where the error is caught locally
// but re-thrown or becomes an unhandled rejection.
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        handleStaleDeploymentError(event.reason, 'unhandled-rejection');
    });
}

/**
 * BRE-5657: report a query failure to PostHog.
 *
 * Before this, a query failing on EVERY navigation was visible only as a
 * `console.error` on the affected machine. Two goals-view queries did exactly
 * that for a full day; nothing server-side logged it (the request never
 * reached the Lambda) and no analytics recorded it, so it was invisible to
 * everyone not looking at that one browser console.
 *
 * Deliberately carries no free text. The query key's FIRST element is the
 * query name; the rest are entity uuids and are dropped. `classification` is a
 * closed set. The error message is excluded entirely - it is user- and
 * server-influenced and routinely embeds record identifiers and user-authored
 * content, which must not reach analytics. `name` is a constructor name, which
 * is safe.
 *
 * Never throws: a telemetry failure must not break the query error path, which
 * is also what triggers the stale-deploy recovery below it.
 */
function captureQueryFailed(error: unknown, query: { queryKey?: readonly unknown[] }): void {
    if (typeof window === 'undefined') return;
    try {
        const { name } = extractErrorDetails(error);
        const classification = isStaleDeploymentError(error, 'query')
            ? 'stale_deployment'
            : isUnexpectedResponseError(error)
                ? 'unexpected_response'
                : 'other';

        const queryName = typeof query.queryKey?.[0] === 'string' ? query.queryKey[0] : 'unknown';

        void import('posthog-js').then(({ default: posthog }) => {
            posthog.capture(ANALYTICS_EVENTS.QUERY_FAILED, {
                query: queryName,
                error_name: name || 'unknown',
                classification,
            });
        }).catch(() => {
            // Swallow - see doc comment.
        });
    } catch {
        // Swallow - see doc comment.
    }
}

/**
 * Next's generic server-action failure (error code E394). Distinct from
 * version skew: this same string covers genuine 5xx, which is exactly why
 * `SERVER_ACTION_SKEW_PATTERN` refuses to match it. Used only to LABEL
 * telemetry, never to trigger a reload.
 */
function isUnexpectedResponseError(error: unknown): boolean {
    const { message } = extractErrorDetails(error);
    return /An unexpected response was received from the server/i.test(message);
}

function makeQueryClient() {
    return new QueryClient({
        // Global query error handler - catches all query errors
        queryCache: new QueryCache({
            onError: (error, query) => {
                console.error('Global Query Error:', error, query.queryKey);
                captureQueryFailed(error, query);
                handleStaleDeploymentError(error, 'query');
            }
        }),
        defaultOptions: {
            queries: {
                // Optimize stale times based on data type patterns
                staleTime: 5 * 60 * 1000, // 5 minutes for general data
                gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
                refetchOnWindowFocus: false,
                refetchOnReconnect: true,
                refetchOnMount: false, // Don't refetch if data is fresh
                retry: (failureCount, error: unknown) => {
                    // Don't retry on auth errors (401, 403) or client errors (4xx)
                    const errorStatus = (error as { status?: number })?.status;
                    if (errorStatus && errorStatus >= 400 && errorStatus < 500) {
                        return false;
                    }
                    // Don't retry stale deployment errors — reload will handle it
                    if (isStaleDeploymentError(error)) return false;
                    // Retry up to 3 times for server errors
                    return failureCount < 3;
                },
                retryDelay: attemptIndex => {
                    // Exponential backoff with jitter
                    const baseDelay = Math.min(1000 * 2 ** attemptIndex, 30000);
                    return baseDelay + Math.random() * 1000;
                },
                // Network mode for better offline support
                networkMode: 'online',
            },
            mutations: {
                retry: (failureCount, error: unknown) => {
                    // Don't retry stale deployment errors — reload will handle it
                    if (isStaleDeploymentError(error)) return false;
                    return failureCount < 1;
                },
                networkMode: 'online',
                onError: (error: unknown) => {
                    console.error('Mutation error:', error);
                    handleStaleDeploymentError(error, 'mutation');
                },
            }
        }
    })
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
    if (isServer) {
        return makeQueryClient();
    } else {
        if (!browserQueryClient) browserQueryClient = makeQueryClient();
        return browserQueryClient;
    }
}

// BRE-4235 PR 1: stale-time presets shared across the app.
// comment-block-ok: pre-existing block; BRE-6695 only swapped one retired
// example on the `cached` line
//
// Spread one of these into the options of a `useQuery` / `prefetchQuery`
// call instead of hardcoding `staleTime: N * 60 * 1000`. Keeps the entire
// app aligned on a small set of well-named freshness buckets:
//
//   - alwaysStale : 0          (data is immediately stale)
//   - fresh       : 30 s       (e.g. inbox, action lists)
//   - stable      : 2 min      (event details, ranked alignments)
//   - cached      : 5 min      (billing, AI usage and credit balances)
//   - long        : 10 min     (transcripts, system-health rollups)
//   - almostStatic: 30 min     (invoice history, near-immutable rollups)
//
// IMPORTANT: this QueryClient sets `defaultOptions.queries.refetchOnMount
// = false`, so a stale query does NOT automatically refetch on remount.
// `alwaysStale` therefore means "always stale, refetch only when something
// else triggers it (refetchOnReconnect, invalidateQueries, manual refetch,
// or a per-query `refetchOnMount: true` opt-in)" — it does not by itself
// force a mount refetch. Pair it with `refetchOnMount: 'always'` if you
// need on-mount refresh behavior.
//
// These are pure `staleTime` snippets — they do NOT override gcTime,
// retry, or refetchOnFocus. Use the richer `QUERY_CONFIG.auth/user/...`
// presets below when you also need to tune those axes. In particular,
// `QUERY_CONFIG.realtime` ALSO sets `refetchInterval: 30s` for polling;
// `QUERY_STALE_PRESETS.alwaysStale` is the pure staleTime=0 version
// without polling. Reach for the right one deliberately.
export const QUERY_STALE_PRESETS = {
    alwaysStale:  { staleTime: 0 },
    fresh:        { staleTime: 30 * 1000 },
    stable:       { staleTime: 2 * 60 * 1000 },
    cached:       { staleTime: 5 * 60 * 1000 },
    long:         { staleTime: 10 * 60 * 1000 },
    almostStatic: { staleTime: 30 * 60 * 1000 },
} as const;

// Optimized query configurations for different data types
export const QUERY_CONFIG = {
    // Authentication data - critical for app functionality
    auth: {
        staleTime: 10 * 60 * 1000, // 10 minutes - auth data is fairly stable
        gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer for auth
        refetchOnWindowFocus: false, // Don't refetch auth on focus to avoid loops
        refetchOnMount: false, // Use prefetched data when available
        retry: (failureCount: number, error: unknown) => {
            // Don't retry stale deployment errors — reload will handle it
            if (isStaleDeploymentError(error)) return false;
            // Don't retry on auth errors (401, 403)
            const errorStatus = (error as { status?: number })?.status;
            const errorMessage = error instanceof Error ? error.message : String(error);

            if (errorStatus === 401 || errorStatus === 403 ||
                errorMessage.includes('401') || errorMessage.includes('403') ||
                errorMessage.includes('unauthorized') || errorMessage.includes('Authentication failed')) {
                return false;
            }
            // Only retry once for other errors
            return failureCount < 1;
        },
        retryDelay: 2000, // Fixed 2 second delay for auth retries
        networkMode: 'online' as const,
    },
    // Static data that changes rarely (5+ minutes)
    static: {
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
    },
    // User data that changes occasionally (2-5 minutes)
    user: {
        staleTime: 3 * 60 * 1000, // 3 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
    },
    // Dynamic data that changes frequently (30 seconds - 2 minutes)
    dynamic: {
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
    },
    // Real-time data that should always be fresh
    realtime: {
        staleTime: 0, // Always stale
        gcTime: 2 * 60 * 1000, // 2 minutes
        refetchInterval: 30 * 1000, // Poll every 30 seconds
    }
};